import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function AdminDashboard({ userInfo }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [userGroupAttributes, setUserGroupAttributes] = useState({});
  const [clientCompanies, setClientCompanies] = useState([]);
  const [jobAssignments, setJobAssignments] = useState([]);
  const [employmentTypes, setEmploymentTypes] = useState([]);
  
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // 管理者権限チェック
    if (!userInfo || (userInfo.role !== 'admin' && userInfo.role !== 'super_admin')) {
      navigate('/time-clock');
      return;
    }

    fetchUsers();
    if (userInfo.role === 'super_admin') {
      fetchClientCompanies();
      fetchEmploymentTypes();
    }
  }, [userInfo, navigate]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/users', {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('ユーザー一覧の取得に失敗しました');
      }

      const data = await response.json();
      setUsers(data);
      
      // ユーザーごとのグループ属性を取得
      await Promise.all(data.map(user => fetchUserGroupAttributes(user.id)));
      
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };
  
  // クライアント企業の取得
  const fetchClientCompanies = async () => {
    try {
      const response = await fetch('/api/admin/client-companies', {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('クライアント企業の取得に失敗しました');
      }

      const data = await response.json();
      setClientCompanies(data);
    } catch (error) {
      console.error('Error fetching client companies:', error);
    }
  };
  
  // 雇用区分の取得
  const fetchEmploymentTypes = async () => {
    try {
      const response = await fetch('/api/admin/employment-types', {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('雇用区分の取得に失敗しました');
      }

      const data = await response.json();
      setEmploymentTypes(data);
    } catch (error) {
      console.error('Error fetching employment types:', error);
    }
  };
  
  // 特定のクライアント企業の担当業務を取得
  const fetchJobAssignments = async (clientCompanyId) => {
    try {
      const response = await fetch(`/api/admin/client-companies/${clientCompanyId}/job-assignments`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('担当業務の取得に失敗しました');
      }

      const data = await response.json();
      setJobAssignments(prevState => ({
        ...prevState,
        [clientCompanyId]: data
      }));
    } catch (error) {
      console.error('Error fetching job assignments:', error);
    }
  };
  
  // ユーザーのグループ属性を取得
  const fetchUserGroupAttributes = async (userId) => {
    try {
      const response = await fetch(`/api/admin/users/${userId}/group-attributes`, {
        headers: {
          'Authorization': localStorage.getItem('token'),
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error(`ユーザーID ${userId} のグループ属性取得に失敗しました`);
        return;
      }

      const data = await response.json();
      
      // クライアント企業IDがあれば、その担当業務を取得
      if (data.client_company_id && !jobAssignments[data.client_company_id]) {
        fetchJobAssignments(data.client_company_id);
      }
      
      setUserGroupAttributes(prevState => ({
        ...prevState,
        [userId]: data
      }));
    } catch (error) {
      console.error(`Error fetching group attributes for user ${userId}:`, error);
    }
  };
  
  // クライアント企業名を取得
  const getClientCompanyName = (id) => {
    const company = clientCompanies.find(c => c.id === id);
    return company ? company.name : '未設定';
  };
  
  // 担当業務名を取得
  const getJobAssignmentName = (clientCompanyId, jobAssignmentId) => {
    if (!jobAssignments[clientCompanyId]) return '未設定';
    const job = jobAssignments[clientCompanyId].find(j => j.id === jobAssignmentId);
    return job ? job.name : '未設定';
  };
  
  // 雇用区分名を取得
  const getEmploymentTypeName = (id) => {
    const type = employmentTypes.find(t => t.id === id);
    return type ? type.name : '未設定';
  };

  const handleUserClick = (userId) => {
    navigate(`/admin/users/${userId}`);
  };

  const handleAddUser = () => {
    navigate('/admin/users/new');
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">読み込み中...</div>;
  }

  if (error) {
    return <div className="text-red-500 p-4">{error}</div>;
  }

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">管理者ダッシュボード</h1>
      
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-xl">ユーザー一覧</h2>
        {userInfo && userInfo.role === 'super_admin' && (
          <button 
            onClick={handleAddUser}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
          >
            新規ユーザー登録
          </button>
        )}
      </div>

      {/* デスクトップ表示 */}
      {!isMobile && (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  社員ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  名前
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  権限
                </th>
                {userInfo && userInfo.role === 'super_admin' && (
                  <>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      クライアント企業
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      担当業務
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      雇用区分
                    </th>
                  </>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  登録日
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map(user => {
                const attributes = userGroupAttributes[user.id] || {};
                return (
                  <tr 
                    key={user.id} 
                    onClick={() => handleUserClick(user.id)}
                    className="hover:bg-gray-100 cursor-pointer"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.employee_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {user.role === 'super_admin' ? 'スーパー管理者' : 
                       user.role === 'admin' ? '管理者' : 'ユーザー'}
                    </td>
                    {userInfo && userInfo.role === 'super_admin' && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {attributes.client_company_id ? 
                            getClientCompanyName(attributes.client_company_id) : 
                            '未設定'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {attributes.client_company_id && attributes.job_assignment_id ? 
                            getJobAssignmentName(attributes.client_company_id, attributes.job_assignment_id) : 
                            '未設定'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {attributes.employment_type_id ? 
                            getEmploymentTypeName(attributes.employment_type_id) : 
                            '未設定'}
                        </td>
                      </>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* モバイル表示 */}
      {isMobile && (
        <div className="space-y-4">
          {users.map(user => {
            const attributes = userGroupAttributes[user.id] || {};
            return (
              <div 
                key={user.id} 
                onClick={() => handleUserClick(user.id)}
                className="bg-white shadow-md rounded-lg p-4 cursor-pointer hover:bg-gray-50"
              >
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-bold">{user.name}</h3>
                  <span className={`text-xs px-2 py-1 rounded ${
                    user.role === 'super_admin' ? 'bg-red-100 text-red-800' : 
                    user.role === 'admin' ? 'bg-blue-100 text-blue-800' : 
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {user.role === 'super_admin' ? 'スーパー管理者' : 
                     user.role === 'admin' ? '管理者' : 'ユーザー'}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  <div className="flex justify-between">
                    <span>社員ID:</span>
                    <span className="font-medium">{user.employee_id}</span>
                  </div>
                  {userInfo && userInfo.role === 'super_admin' && (
                    <>
                      <div className="flex justify-between mt-1">
                        <span>クライアント企業:</span>
                        <span className="font-medium">
                          {attributes.client_company_id ? 
                            getClientCompanyName(attributes.client_company_id) : 
                            '未設定'}
                        </span>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span>担当業務:</span>
                        <span className="font-medium">
                          {attributes.client_company_id && attributes.job_assignment_id ? 
                            getJobAssignmentName(attributes.client_company_id, attributes.job_assignment_id) : 
                            '未設定'}
                        </span>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span>雇用区分:</span>
                        <span className="font-medium">
                          {attributes.employment_type_id ? 
                            getEmploymentTypeName(attributes.employment_type_id) : 
                            '未設定'}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between mt-1">
                    <span>登録日:</span>
                    <span className="font-medium">{new Date(user.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AdminDashboard; 